import { useQueries, useQuery } from "@tanstack/react-query";
import {
  getActivityInclusions,
  getActivityExclusions,
  getActivityItinerary,
  getActivityTicketTypePrices,
  getActivityTickets,
} from "../activityService";

const formatPrice = (priceRow) => {
  if (!priceRow) return "—";
  if (priceRow.rate_type === "full" || Number(priceRow.full_rate) > 0) {
    const val = priceRow.full_rate || priceRow.adult_price;
    if (val) return `₹${Number(val).toLocaleString("en-IN")}`;
  }
  const adult = priceRow.adult_price;
  if (adult) return `₹${Number(adult).toLocaleString("en-IN")}`;
  return "—";
};

export function useTicketTypeSummary(activityId) {
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ["activityTickets", activityId],
    queryFn: () => getActivityTickets(activityId),
    enabled: !!activityId,
  });

  const tickets = ticketsData?.data || [];

  const inclusionQueries = useQueries({
    queries: tickets.map((t) => ({
      queryKey: ["activityInclusions", activityId, t.id],
      queryFn: () => getActivityInclusions(activityId, t.id),
      enabled: !!activityId && !!t.id,
    })),
  });

  const exclusionQueries = useQueries({
    queries: tickets.map((t) => ({
      queryKey: ["activityExclusions", activityId, t.id],
      queryFn: () => getActivityExclusions(activityId, t.id),
      enabled: !!activityId && !!t.id,
    })),
  });

  const itineraryQueries = useQueries({
    queries: tickets.map((t) => ({
      queryKey: ["activityItinerary", activityId, t.id],
      queryFn: () => getActivityItinerary(activityId, t.id),
      enabled: !!activityId && !!t.id,
    })),
  });

  const { data: pricesData, isLoading: pricesLoading } = useQuery({
    queryKey: ["activityTicketTypePrices", activityId],
    queryFn: () => getActivityTicketTypePrices(activityId),
    enabled: !!activityId,
  });

  const prices = pricesData?.data || [];

  const summaries = tickets.map((ticket, index) => {
    const priceRow = prices.find(
      (p) =>
        Number(p.activity_ticket_type_id) === Number(ticket.id) ||
        Number(p.ticket_type_id) === Number(ticket.id)
    );
    const inclusions = inclusionQueries[index]?.data?.data || [];
    const exclusions = exclusionQueries[index]?.data?.data || [];
    const itinerary = itineraryQueries[index]?.data?.data || [];

    return {
      id: ticket.id,
      name: ticket.name,
      description: ticket.description,
      displayPrice: formatPrice(priceRow),
      inclusionCount: inclusions.length,
      exclusionCount: exclusions.length,
      itineraryCount: itinerary.length,
      maxBookings: priceRow?.maximum_allowed_bookings_per_user,
    };
  });

  const isDetailsLoading =
    pricesLoading ||
    inclusionQueries.some((q) => q.isLoading) ||
    exclusionQueries.some((q) => q.isLoading) ||
    itineraryQueries.some((q) => q.isLoading);

  const isInitialLoading = ticketsLoading && tickets.length === 0;

  return {
    summaries,
    tickets,
    isLoading: isInitialLoading,
    isDetailsLoading,
  };
}
